import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EntityLink } from "@/components/shared/EntityLink";
import PrintDialog from "@/components/shared/PrintDialog";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NumericInput } from "@/components/ui/numeric-input";
import { trpcClient } from "@/lib/trpc";
import { formatDate, formatMoney } from "@/lib/utils";
import { toVnDateString } from "@apv-erp/status";

/** Hình thức chi (cùng danh sách với Thu chi & Sổ quỹ) */
export const VENDOR_PAYMENT_METHODS = [
  { id: "CHUYEN_KHOAN", label: "Chuyển khoản" },
  { id: "TIEN_MAT", label: "Tiền mặt" },
  { id: "CAN_TRU", label: "Cấn trừ" },
];
export const vendorPaymentMethodLabel = (id?: string | null) =>
  VENDOR_PAYMENT_METHODS.find((m) => m.id === id)?.label || id || "—";

const NO_DOC = "__none__";

/**
 * Phiếu chi trả nhà cung cấp (04/11/2026): chọn chứng từ (đơn mua / đơn gia công còn phải trả) hoặc trả chung,
 * không chi vượt nợ còn lại (máy chủ kiểm lại); giảm công nợ NCC và kỳ đối soát.
 */
export function VendorPaymentDialog({
  open,
  onOpenChange,
  vendor,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendor: { id: string; code?: string; name: string; outstanding: number } | null;
}) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState<number>(0);
  const [method, setMethod] = useState("CHUYEN_KHOAN");
  const [paymentDate, setPaymentDate] = useState(toVnDateString());
  const [doc, setDoc] = useState<string>(NO_DOC);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && vendor) {
      setAmount(Math.max(0, Math.round(vendor.outstanding)));
      setMethod("CHUYEN_KHOAN");
      setPaymentDate(toVnDateString());
      setDoc(NO_DOC);
      setNotes("");
    }
  }, [open, vendor]);

  // Chứng từ còn phải trả của NCC: đơn mua + đơn gia công (phải trả > đã trả)
  const { data: docs = [] } = useQuery({
    queryKey: ["vendor-payable-docs", vendor?.id],
    enabled: open && Boolean(vendor?.id),
    queryFn: async () => {
      const [pos, osps]: any[] = await Promise.all([
        (trpcClient as any).purchaseOrders.list.query({ vendorId: vendor!.id, page: 1, limit: 200 }),
        (trpcClient as any).outsourcePos.list.query({ vendorId: vendor!.id, page: 1, limit: 200 }),
      ]);
      const pick = (items: any[], kind: "PO" | "OSP") =>
        (items || [])
          .filter((d) => d.paymentInfo && d.paymentInfo.payable - d.paymentInfo.paid > 0)
          .map((d) => ({ key: `${kind}:${d.id}`, id: d.id, kind, code: d.poId, left: d.paymentInfo.payable - d.paymentInfo.paid }));
      return [...pick(pos?.items, "PO"), ...pick(osps?.items, "OSP")];
    },
  });
  const selectedDoc = useMemo(() => docs.find((d: any) => d.key === doc) || null, [docs, doc]);
  useEffect(() => {
    if (selectedDoc) setAmount(Math.max(0, Math.min(Math.round(selectedDoc.left), Math.round(vendor?.outstanding || 0))));
  }, [selectedDoc, vendor]);

  const cap = Math.round(Math.min(vendor?.outstanding || 0, selectedDoc ? selectedDoc.left : Infinity));
  const invalid = !(amount > 0) || amount > cap;

  const submit = async () => {
    if (!vendor) return;
    setSaving(true);
    try {
      const res: any = await (trpcClient as any).vendors.createVendorPayment.mutate({
        vendorId: vendor.id,
        amount,
        method,
        paymentDate,
        description: notes.trim() || undefined,
        purchaseOrderId: selectedDoc?.kind === "PO" ? selectedDoc.id : undefined,
        outsourcePoId: selectedDoc?.kind === "OSP" ? selectedDoc.id : undefined,
      });
      toast.success(`Đã lập phiếu chi ${res?.paymentNo || ""} trả ${vendor.name}: ${formatMoney(amount)} đ`);
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
      queryClient.invalidateQueries({ queryKey: ["vendor-payments"] });
      queryClient.invalidateQueries({ queryKey: ["vendor-payable-docs"] });
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || "Không lập được phiếu chi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Lập phiếu chi trả {vendor?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div className="flex justify-between rounded-md bg-rose-50 px-3 py-2">
            <span className="text-slate-600">Nợ còn lại</span>
            <span className="font-bold text-rose-700 tabular-nums" data-testid="vendor-payment-outstanding">
              {formatMoney(vendor?.outstanding || 0)} đ
            </span>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-700">Chứng từ được trả</label>
            <Select value={doc} onValueChange={setDoc}>
              <SelectTrigger data-testid="vendor-payment-doc">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_DOC}>Trả chung (không gắn chứng từ)</SelectItem>
                {docs.map((d: any) => (
                  <SelectItem key={d.key} value={d.key}>
                    {d.kind === "PO" ? "Đơn mua" : "Đơn gia công"} {d.code} — còn {formatMoney(d.left)} đ
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700" htmlFor="vendor-payment-amount">
                Số tiền chi (tối đa {formatMoney(cap)} đ)
              </label>
              <NumericInput id="vendor-payment-amount" data-testid="vendor-payment-amount" value={amount} onValueChange={(v) => setAmount(v.floatValue || 0)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700">Hình thức</label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VENDOR_PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700" htmlFor="vendor-payment-date">
                Ngày chi
              </label>
              <Input id="vendor-payment-date" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700" htmlFor="vendor-payment-notes">
                Ghi chú
              </label>
              <Input id="vendor-payment-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="VD: Thanh toán đợt 1" />
            </div>
          </div>
          {amount > cap && <p className="text-xs text-rose-600">Số tiền vượt số còn phải trả.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Huỷ
          </Button>
          <Button disabled={invalid || saving} onClick={submit} data-testid="vendor-payment-submit">
            {saving ? "Đang lưu..." : "Lưu phiếu chi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Danh sách phiếu chi NCC: mã, ngày, NCC, chứng từ, hình thức, số tiền, người lập, [Huỷ] (hộp thoại ghi lý do) */
export function VendorPaymentsPanel({ search, canCancel, onTotal }: { search?: string; canCancel: boolean; onTotal?: (total: number) => void }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["vendor-payments", search || ""],
    queryFn: () => (trpcClient as any).vendors.listVendorPayments.query({ search: search || undefined, page: 1, limit: 200 }),
  });
  const items: any[] = (data as any)?.items || [];
  const total = Number((data as any)?.total ?? items.length);
  useEffect(() => {
    onTotal?.(total);
  }, [total, onTotal]);
  const [cancelling, setCancelling] = useState<any | null>(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState<any | null>(null);

  const openCancel = (p: any) => {
    setReason("");
    setCancelling(p);
  };
  const confirmCancel = async () => {
    if (!cancelling) return;
    setSaving(true);
    try {
      await (trpcClient as any).vendors.cancelVendorPayment.mutate({ id: cancelling.id, reason: reason.trim() });
      toast.success(`Đã huỷ phiếu chi ${cancelling.paymentNo}, công nợ NCC được hoàn lại`);
      queryClient.invalidateQueries({ queryKey: ["vendor-payments"] });
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
      queryClient.invalidateQueries({ queryKey: ["vendor-debts"] });
      setCancelling(null);
    } catch (err: any) {
      toast.error(err?.message || "Không huỷ được phiếu chi");
    } finally {
      setSaving(false);
    }
  };

  const docLink = (p: any) =>
    p.purchaseOrderId ? (
      <EntityLink entityType="purchaseOrder" id={p.purchaseOrderId} code={p.referenceId} className="whitespace-nowrap" />
    ) : p.outsourcePoId ? (
      <EntityLink entityType="outsourceOrder" id={p.outsourcePoId} code={p.referenceId} className="whitespace-nowrap" />
    ) : (
      <span className="text-slate-500">{p.referenceId || "Trả chung"}</span>
    );
  const rowActions = (p: any) => (
    <span className="inline-flex items-center gap-1">
      <Button variant="ghost" size="sm" className="h-7" onClick={() => setPrinting(p)} data-testid="vendor-payment-print">
        In
      </Button>
      {canCancel && (
        <Button variant="ghost" size="sm" className="h-7 text-rose-600" onClick={() => openCancel(p)} data-testid="vendor-payment-cancel">
          Huỷ
        </Button>
      )}
    </span>
  );
  // Phiếu chi in theo mẫu Phiếu chi chung (TransactionPrintTemplate): người nhận tiền = nhà cung cấp
  const printData = (p: any) =>
    p && {
      receiptId: p.paymentNo,
      type: "EXPENSE",
      amount: Number(p.amount || 0),
      receiptDate: p.paymentDate,
      paymentMethod: p.method,
      referenceNumber: p.referenceId,
      notes: p.description || `Thanh toán cho ${p.vendor?.name || "nhà cung cấp"}${p.referenceId ? ` theo ${p.purchaseOrderId ? "đơn mua" : p.outsourcePoId ? "đơn gia công" : "chứng từ"} ${p.referenceId}` : ""}`,
      originalDocsLabel: p.referenceId ? `${p.purchaseOrderId ? "Đơn mua" : p.outsourcePoId ? "Đơn gia công" : "Chứng từ"} ${p.referenceId} và hoá đơn của nhà cung cấp` : undefined,
      vendor: p.vendor,
      creator: p.creatorName ? { fullName: p.creatorName } : undefined,
    };

  if (isLoading) return <div className="p-6 text-center text-sm text-slate-500">Đang tải phiếu chi...</div>;
  if (!items.length) return <div className="p-6 text-center text-sm text-slate-400">Chưa có phiếu chi nhà cung cấp</div>;
  return (
    <>
      <div className="hidden md:block overflow-auto">
        <table className="w-full text-xs" data-testid="vendor-payments-table">
          <thead className="sticky top-0 z-10 bg-slate-50 text-slate-600 uppercase text-[10px]">
            <tr>
              <th className="px-3 py-2 text-left">Mã phiếu</th>
              <th className="px-3 py-2 text-left">Ngày chi</th>
              <th className="px-3 py-2 text-left">Nhà cung cấp</th>
              <th className="px-3 py-2 text-left">Chứng từ</th>
              <th className="px-3 py-2 text-left">Hình thức</th>
              <th className="px-3 py-2 text-right">Số tiền</th>
              <th className="px-3 py-2 text-left">Người lập</th>
              <th className="px-3 py-2 text-left">Ghi chú</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-slate-100">
                <td className="px-3 py-2 font-mono font-semibold whitespace-nowrap">{p.paymentNo}</td>
                <td className="px-3 py-2 whitespace-nowrap">{formatDate(p.paymentDate)}</td>
                <td className="px-3 py-2">
                  <EntityLink entityType="vendor" id={p.vendorId} code={p.vendor?.code} label={p.vendor?.name} />
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{docLink(p)}</td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className="text-[10px]">
                    {vendorPaymentMethodLabel(p.method)}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right font-bold tabular-nums text-emerald-700 whitespace-nowrap">{formatMoney(p.amount)}</td>
                <td className="px-3 py-2">{p.creatorName || "—"}</td>
                <td className="px-3 py-2 text-slate-500">{p.description || ""}</td>
                <td className="px-3 py-2 text-right">{rowActions(p)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden divide-y divide-slate-100" data-testid="vendor-payments-cards">
        {items.map((p) => (
          <div key={p.id} className="p-3 text-xs space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono font-semibold">{p.paymentNo}</span>
              <span className="font-bold tabular-nums text-emerald-700">{formatMoney(p.amount)} đ</span>
            </div>
            <div className="text-slate-700">{p.vendor?.name}</div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-slate-500">
              <span>{formatDate(p.paymentDate)}</span>
              <span>·</span>
              {docLink(p)}
              <span>·</span>
              <span>{vendorPaymentMethodLabel(p.method)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">{p.creatorName || ""}</span>
              {rowActions(p)}
            </div>
          </div>
        ))}
      </div>
      <PrintDialog open={Boolean(printing)} onClose={() => setPrinting(null)} type="receipt" data={printData(printing)} documentTitle={printing?.paymentNo} />
      <Dialog open={Boolean(cancelling)} onOpenChange={(o) => !o && setCancelling(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Huỷ phiếu chi {cancelling?.paymentNo}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2 text-sm">
            <p className="text-slate-600">
              Huỷ phiếu chi {formatMoney(cancelling?.amount || 0)} đ trả {cancelling?.vendor?.name}: công nợ nhà cung cấp được cộng lại số tiền này.
            </p>
            <label className="text-xs font-medium text-slate-700" htmlFor="vendor-payment-cancel-reason">
              Lý do huỷ (tối thiểu 5 ký tự)
            </label>
            <Textarea id="vendor-payment-cancel-reason" data-testid="vendor-payment-cancel-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelling(null)}>
              Đóng
            </Button>
            <Button variant="destructive" disabled={reason.trim().length < 5 || saving} onClick={confirmCancel} data-testid="vendor-payment-cancel-confirm">
              {saving ? "Đang huỷ..." : "Huỷ phiếu chi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
